const GENERIC_CLIPBOARD_FILENAME = /^image\.[a-z0-9]+$/i
const PASTED_IMAGE_PREFIX = "Pasted image"
const EXTENSION_BY_IMAGE_SUBTYPE: Record<string, string> = {
  jpeg: "jpg",
  "x-icon": "ico",
  "vnd.microsoft.icon": "ico",
}

export type ClipboardSource = Pick<DataTransfer, "files" | "getData">

function hasGenericName(file: File): boolean {
  return file.name === "" || GENERIC_CLIPBOARD_FILENAME.test(file.name)
}

export function selectPastedFiles(clipboard: ClipboardSource): File[] {
  const files = Array.from(clipboard.files)
  const hasText = clipboard.getData("text/plain") !== ""
  return hasText ? files.filter((file) => !hasGenericName(file)) : files
}

function extensionForImageType(mimeType: string): string {
  const subtype = mimeType.slice(mimeType.indexOf("/") + 1).toLowerCase()
  const token = subtype.split("+")[0].split(".").pop() ?? ""
  return EXTENSION_BY_IMAGE_SUBTYPE[subtype] ?? token.replace(/^x-/, "").replace(/[^a-z0-9]/g, "")
}

function twoDigits(value: number): string {
  return String(value).padStart(2, "0")
}

function pastedImageBaseName(now: Date): string {
  const date = `${now.getFullYear()}-${twoDigits(now.getMonth() + 1)}-${twoDigits(now.getDate())}`
  const time = `${twoDigits(now.getHours())}.${twoDigits(now.getMinutes())}`
  return `${PASTED_IMAGE_PREFIX} ${date} ${time}`
}

function claimUniqueName(baseName: string, extension: string, takenNames: Set<string>): string {
  const suffix = extension === "" ? "" : `.${extension}`
  let candidate = `${baseName}${suffix}`
  for (let repeat = 2; takenNames.has(candidate); repeat += 1) candidate = `${baseName} (${repeat})${suffix}`
  takenNames.add(candidate)
  return candidate
}

export function nameClipboardFiles(files: readonly File[], queuedNames: readonly string[], now: Date): File[] {
  const takenNames = new Set(queuedNames)
  return files.map((file) => {
    if (!file.type.startsWith("image/") || !hasGenericName(file)) return file
    const name = claimUniqueName(pastedImageBaseName(now), extensionForImageType(file.type), takenNames)
    return new File([file], name, { type: file.type, lastModified: file.lastModified })
  })
}
